/**
 * 三重県の料理12品。
 *
 * 三重は伊勢志摩・伊賀・中南勢・北勢・東紀州で食文化がはっきり分かれる。
 * 五つの圏域から均等に取り、どこか一つに寄らないようにした。
 *
 * 他県と重ならないように外したもの。
 *  - 伊勢えび … 千葉が「外房イセエビ」で公開済み
 *  - ふぐ … 福岡・静岡・福井ですでに3県
 *  - さんま寿司・めはりずし … 和歌山で公開済み
 *  - こけらずし … 和歌山の「こけら寿司」と同じ名前・同じ形
 *  - 茶粥 … 奈良で公開済み
 *  - のっぺい … 新潟の「のっぺ」と重なる
 *  - たこ飯 … 岡山の「たこめし」と重なる
 *  - 豆腐でんがく … 愛知の「味噌田楽」と同じもの。愛知に割り当てた
 *  - そば・そうめん・ラーメン … すでに他県に多い
 *
 * 松阪牛は入れていない。
 *  松阪市公式にはステーキ・すき焼き・焼肉の動画しかなく、
 *  「三重固有の食べ方」の説明文が見つからなかった。
 *  和牛のすき焼きはすでに8県あるので、確認できない以上は入れない。
 *
 * 的矢かきも入れていない。志摩市・三重県の公式ページに到達できず、
 *  広島の焼きがき・山形の岩牡蠣との見た目の違いを示せなかった。
 *
 * こうなごは「この5、6年は禁漁となっている（2021年時点）」と
 * 農水省のページにある。「今も獲れる」とは書かない。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：落花生の煮豆・ないしょ餅・あほだき・こうなごのくぎ煮・いばら餅・ふき俵・ガラガラおろし・さめなます・あらめ巻き・魚のじふ（店で食べられる品に差し替え）
 * - 赤福餅：「こし餡」と材料を書かなかった：開いた観光連盟のページで餡の種類を確かめていないため（赤福の商品ページは店のサイトなので出典にしない）
 * - 赤福餅：朔日餅は赤福餅とは別の商品なので書かなかった
 * - 赤福餅：値段・営業時間は書かない
 * - 津ぎょうざ：B-1グランプリの受賞歴は、開いた公式ページで確かめていないので書かなかった
 * - 津ぎょうざ：2008年の津まつりでの販売個数など、細かい数字は図鑑には入れなかった
 * - 亀山みそ焼きうどん：亀山みそ焼きうどん本舗の公式URLは http のみなので使わなかった
 * - 亀山みそ焼きうどん：「汁はなく」は観光三重の「鉄板等で焼き」からの言い換え。気になる場合はこの一文を削る
 * - 焼きはまぐり：松かさで焼くなどの焼き方は、開いた公式ページに書いていないので書かなかった
 * - 焼きはまぐり：徳川将軍家への献上は市のページにあるが、字数の都合で入れなかった（使ってもよい）
 * - 松阪鶏焼き肉：味噌の種類（赤味噌・豆味噌）は、開いた公式ページで確かめられなかったので書かなかった
 * - 四日市とんてき：最初に出した店の名前は書かない（店の宣伝になるため）
 * - 四日市とんてき：四日市観光協会のページ（kanko-yokkaichi.com）は 403 で開けず、四日市市の広報PDFは中身を読めなかったので使っていない
 * - 焼き牡蠣（浦村かき・的矢かき）：牡蠣小屋や養殖場の名前は書かない
 * - 焼き牡蠣（浦村かき・的矢かき）：的矢かきの浄化法の考案年・特許の話は、開いた公式ページにないので書かなかった
 * - あのりふぐ：菊の花のような盛り付け・もみじおろし・ねぎは、開いた公式ページで確かめていないので説明文に書かなかった（絵にはある）
 * - 松阪牛焼肉：「日本三大和牛」などの称号は、開いた公式ページで確かめていないので書かなかった
 * - 松阪牛焼肉：特産松阪牛（900日以上の肥育）は焼肉一般の説明ではないので書かなかった
 * - 伊勢海老：漁獲量の全国1位は、年によって数字が変わり、開いたページの数字も平成23年のものなので書かなかった
 * - 伊勢海老：「伊勢」の名の由来は、開いた公式ページにないので書かなかった
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { mieSceneryTour } from './mieScenes';
import { MIE_FOOD_HULLS } from './mieFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'akafuku-mochi',
    level: 1,
    name: '赤福餅',
    shortName: '赤福餅',
    image: '/assets/foods/mie/01_akafuku_mochi.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '伊勢の餅菓子で、1707（宝永4）年に生まれたとされる。形は伊勢神宮の神域を流れる五十鈴川のせせらぎをかたどり、餡につけた三筋は清流を、白い餅は川底の小石を表す。',
    source:
      'https://www.kankomie.or.jp/report/792',
    icon: { label: '赤福餅', shape: 'mochi', baseColor: '#5a3a3a', accentColor: '#f4efe6' },
    reward: {
      unlockScore: 200,
      area: '伊勢市',
      headline: '三筋は五十鈴川の流れ',
      tip: '店内で出す赤福餅は、3年以上修業した「餅入れさん」が手作業で作る。本店には座敷席と縁側席があり、縁側からは五十鈴川を眺められる。',
      links: searchLinks('伊勢市', '赤福餅'),
      official: [{ label: '観光三重「赤福」', url: 'https://www.kankomie.or.jp/spot/41' }],
    },
  },
  {
    id: 'tsu-gyoza',
    level: 2,
    name: '津ぎょうざ',
    shortName: '津ぎょうざ',
    image: '/assets/foods/mie/02_tsu_gyoza.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '直径15cmの大きな皮で具を包み、油で揚げた大きな揚げ餃子。津ぎょうざ協会の定義は「直径15cmの皮を使う」「揚げ餃子である」の2つで、中の具は店ごとに違う。',
    source:
      'https://www.kankomie.or.jp/report/929',
    icon: { label: '津ぎょうざ', shape: 'dumpling', baseColor: '#c98a3a', accentColor: '#7a4a1e' },
    reward: {
      unlockScore: 500,
      area: '津市',
      headline: '給食から生まれた大きな揚げ餃子',
      tip: '1985（昭和60）年ごろ津市の学校給食に登場した。ふつうの餃子なら3〜4個作るところを1個で済むよう、直径15cmになったといわれる。',
      links: searchLinks('津市', '津ぎょうざ'),
      official: [{ label: '観光三重「津ぎょうざ」', url: 'https://www.kankomie.or.jp/spot/19639' }],
    },
  },
  {
    id: 'kameyama-miso-yakiudon',
    level: 3,
    name: '亀山みそ焼きうどん',
    shortName: 'みそ焼きうどん',
    image: '/assets/foods/mie/03_kameyama_miso_yakiudon.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'うどんを野菜や肉と一緒に鉄板などで焼き、赤味噌ベースの味噌だれで味をつける。味噌だれは店ごとに独自の調合があり、ピリッと甘辛い。',
    source:
      'https://www.kankomie.or.jp/spot/21536',
    icon: { label: '焼きうどん', shape: 'noodle-bowl', baseColor: '#8a4a2a', accentColor: '#6aa04a' },
    reward: {
      unlockScore: 1000,
      area: '亀山市',
      headline: '赤味噌だれで焼くうどん',
      tip: '国道を走るトラック運転手たちが好んで食べていたものが、地元の味として親しまれるようになったと亀山市は紹介している。',
      links: searchLinks('亀山市', '亀山みそ焼きうどん'),
      official: [{ label: '亀山市「亀山ブランド認定商品『みそ焼きうどん』」', url: 'https://www.city.kameyama.mie.jp/docs/2021081100029/' }],
    },
  },
  {
    id: 'ise-udon',
    level: 4,
    name: '伊勢うどん',
    shortName: '伊勢うどん',
    image: '/assets/foods/mie/04_ise_udon.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '黒いたれを、太い麺にからめる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/iseudon_mie.html',
    icon: { label: '伊勢うどん', shape: 'noodle-bowl', baseColor: '#2a2018', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 1800,
      area: '伊勢市・鳥羽市',
      headline: '普通のうどんの三倍はある太く柔らかい麺に、たまり醤油の黒いたれをからめた伊勢の一杯。',
      tip: 'たれは昆布、雑節、みりん、たまり醤油、砂糖で作る。もとは腰の無い太いうどんに味噌だまりを絡ませて食べていた。江戸時代の参宮客向けに、ゆで続けた麺にたまりを掛けてすぐに食べられる店が現れた。昭和47年（1972年）に伊勢市麺類飲食業組合が統一名を決めた。一年中食べられる伊勢市民のソウルフード。',
      links: searchLinks('伊勢市', '伊勢うどん'),
    },
  },
  {
    id: 'kuwana-yaki-hamaguri',
    level: 5,
    name: '焼きはまぐり',
    shortName: '焼きはまぐり',
    image: '/assets/foods/mie/05_kuwana_yaki_hamaguri.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '桑名の赤須賀漁港で揚がる「地はまぐり」は、ヤマトハマグリという日本古来の種。内湾の汽水域で育つため殻が薄く、身はぷっくりと柔らかい。「桑名焼き蛤」は文化庁の100年フードに認定された。',
    source:
      'https://www.city.kuwana.lg.jp/kanko/news/20250401.html',
    icon: { label: '焼き蛤', shape: 'fish', baseColor: '#d9c3a0', accentColor: '#e0a040' },
    reward: {
      unlockScore: 3000,
      area: '桑名市',
      headline: 'その手は桑名の焼き蛤',
      tip: '「その手は桑名の焼きハマグリ」という言葉があるほど、江戸時代から名物として知られた。その味は「浜の栗」と称される。',
      links: searchLinks('桑名市', '焼きはまぐり'),
      official: [{ label: '桑名市観光サイト「桑名焼き蛤」', url: 'https://www.city.kuwana.lg.jp/kanko/news/20250401.html' }],
    },
  },
  {
    id: 'matsusaka-tori-yakiniku',
    level: 6,
    name: '松阪鶏焼き肉',
    shortName: '松阪鶏焼き肉',
    image: '/assets/foods/mie/06_matsusaka_tori_yakiniku.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '鶏肉に店ごとの甘辛い味噌だれをつけ、串を通さずに焼いて食べる。「味噌だれであること」「あみ焼きであること」「鶏焼き肉を専門に置くこと」が松阪鶏焼き肉の店の条件とされる。',
    source:
      'https://matsusaka-info.jp/fine_articles/detail/post-2-11.html',
    icon: { label: '鶏焼き肉', shape: 'feast', baseColor: '#9a4a2a', accentColor: '#e8d0a0' },
    reward: {
      unlockScore: 4500,
      area: '松阪市',
      headline: '味噌だれで焼く鶏',
      tip: '昭和のころから養鶏が盛んで、卵を産まなくなった鶏を味噌だれに絡めて網焼きにする習慣があった。焼いた肉は白いご飯にのせて食べる。',
      links: searchLinks('松阪市', '鶏焼き肉'),
    },
  },
  {
    id: 'yokkaichi-tonteki',
    level: 7,
    name: '四日市とんてき',
    shortName: '四日市とんてき',
    image: '/assets/foods/mie/07_yokkaichi_tonteki.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '厚切りの豚肉をソテーし、にんにくと一緒に濃いめのソースを絡め、たっぷりの千切りキャベツを添える。戦後間もないころ、四日市の市街地の店で出されたのが始まりとされる。',
    source:
      'https://www.kankomie.or.jp/report/860',
    icon: { label: 'とんてき', shape: 'curry-plate', baseColor: '#3a2418', accentColor: '#a8d070' },
    reward: {
      unlockScore: 6500,
      area: '四日市市',
      headline: 'グローブ形の厚い豚肉',
      tip: 'グローブ状の独特な形をした肉が特徴とされる。一般社団法人四日市とんてき協会が2008年から四日市とんてきを広めている。',
      links: searchLinks('四日市市', 'とんてき'),
      official: [{ label: '一般社団法人 四日市とんてき協会', url: 'https://tonteki.com/' }],
    },
  },
  {
    id: 'toba-shima-yaki-gaki',
    level: 8,
    name: '焼き牡蠣（浦村かき・的矢かき）',
    shortName: '焼き牡蠣',
    image: '/assets/foods/mie/08_toba_shima_yaki_gaki.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '浦村かきは鳥羽市の生浦湾で養殖される1年物の牡蠣。臭みやえぐみがなく、身は柔らかい。10月ごろから水揚げされ、年明けから2月ごろに身が大きくなる。焼いた牡蠣を食べ放題で味わえる牡蠣小屋がある。',
    source:
      'https://www.toba.gr.jp/feature/oyster/',
    icon: { label: '焼き牡蠣', shape: 'fish', baseColor: '#d8d4c8', accentColor: '#7a7468' },
    reward: {
      unlockScore: 9000,
      area: '鳥羽市浦村町／志摩市的矢',
      headline: '1年で育つ浦村の牡蠣',
      tip: '志摩の的矢かきは、紫外線で殺菌した海水のシャワーを約20時間浴びせて雑菌を吐き出させ、生で食べられるまで浄化している。',
      links: searchLinks('鳥羽市浦村町', '焼き牡蠣'),
      official: [{ label: '鳥羽市観光協会「浦村の牡蠣」', url: 'https://www.toba.gr.jp/feature/oyster/' }],
    },
  },
  {
    id: 'tekone-zushi',
    level: 9,
    name: 'てこねずし',
    shortName: 'てこねずし',
    image: '/assets/foods/mie/09_tekone_zushi.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '船の上で、手で混ぜたのが始まりとされる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/tekone_zushi_mie.html',
    icon: { label: 'てこねずし', shape: 'rice-bowl', baseColor: '#8a2828', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 12000,
      area: '志摩市・鳥羽市',
      headline: '醤油に漬けたカツオの赤身を酢飯に並べ、しそと海苔を散らした漁師の飯。',
      tip: '漁師が船上で魚をさばいて手で混ぜたことから名がついた。農山漁村の郷土料理百選に選定されている。三重県はカツオの漁獲地で、熊野灘では一本釣りと曳き縄釣りが盛ん。もとは漁師が忙しい作業の合間に手軽に食べる食事だったが、現在は木桶（飯切り）を使って作り、宴会でも出される。',
      links: searchLinks('志摩市', 'てこねずし'),
    },
  },
  {
    id: 'anori-fugu',
    level: 10,
    name: 'あのりふぐ',
    shortName: 'あのりふぐ',
    image: '/assets/foods/mie/10_anori_fugu.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '伊勢湾を含む遠州灘から熊野灘の海域でとれる、体重700g以上の天然トラフグ。てっさ（ふぐ刺し）はもっちりとした歯ごたえで、噛むほどに旨みが出る。店で味わえるのは10月から3月まで。',
    source:
      'https://www.kankomie.or.jp/season/article/189',
    icon: { label: 'ふぐ刺し', shape: 'sashimi-platter', baseColor: '#eef0ee', accentColor: '#d8603a' },
    reward: {
      unlockScore: 16000,
      area: '志摩市',
      headline: '安乗の天然トラフグ',
      tip: '三重外湾漁業協同組合の安乗漁港を中心に水揚げされることから「あのりふぐ」と呼ばれる。長い幹縄に釣針を付けた延縄で釣る。',
      links: searchLinks('志摩市', 'あのりふぐ'),
      official: [{ label: '三重ブランド「あのりふぐ」', url: 'https://www.miebrand.jp/certified_product/11' }, { label: 'あのりふぐ協議会', url: 'https://anorifugu.info/' }],
    },
  },
  {
    id: 'matsusaka-ushi-yakiniku',
    level: 11,
    name: '松阪牛焼肉',
    shortName: '松阪牛焼肉',
    image: '/assets/foods/mie/11_matsusaka_ushi_yakiniku.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '子牛を産んでいない雌の牛を育てた肉で、赤い肉に白い脂肪が散らばった霜降りが特徴。長く育てた牛ほど脂が溶けやすく、甘くいい香りがする。',
    source:
      'https://www.city.matsusaka.mie.jp/site/matsusakaushi/donna.html',
    icon: { label: '松阪牛', shape: 'feast', baseColor: '#e8a0a0', accentColor: '#fbf0ea' },
    reward: {
      unlockScore: 22000,
      area: '松阪市',
      headline: '白い脂が散る霜降り',
      tip: '松阪牛と名乗れるのは、黒毛和種の未経産の雌牛で、生後12か月齢までに松阪牛生産区域に入り、そこでの肥育期間が最長・最終のもの。個体識別管理システムへの登録も条件。',
      links: searchLinks('松阪市', '松阪牛 焼肉'),
      official: [{ label: '松阪市「松阪牛とは」', url: 'https://www.city.matsusaka.mie.jp/site/matsusakaushi/matsusakaushitowa.html' }],
    },
  },
  {
    id: 'ise-ebi',
    level: 12,
    name: '伊勢海老',
    shortName: '伊勢海老',
    image: '/assets/foods/mie/12_ise_ebi.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '三重県では志摩半島を中心にとれる。産卵期を守るため5月1日から9月30日まで（鳥羽市離島地域以北の海は9月15日まで）を禁漁にしていて、この決まりは他県より厳しい。網に稚えびが混ざれば放流する。',
    source:
      'https://www.miebrand.jp/certified_product/18',
    icon: { label: '伊勢海老', shape: 'feast', baseColor: '#d0302a', accentColor: '#f6f0e6' },
    reward: {
      unlockScore: 30000,
      area: '志摩市／鳥羽市',
      headline: '産卵期は漁を休む',
      tip: '赤い刺し網を使い、夕方から夜にかけてとる。刺身のほか、強火で豪快に直焼きにしても食べられる。旬は10月から4月。',
      links: searchLinks('志摩市', '伊勢海老'),
      official: [{ label: '三重ブランド「伊勢えび」', url: 'https://www.miebrand.jp/certified_product/18' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const mieFoodStage: StageDefinition = {
  regionId: 'mie',
  regionName: '三重県',
  mode: 'food',
  title: '三重めしマージ',
  theme: {
    primaryColor: '#2a6a6a',
    secondaryColor: '#c8b070',
    accentColor: '#e8ddc0',
    backgroundColor: '#0f1618',
  },
  /** 背景ツアー「三重めぐり」。軸は一日の時間。日の出の夫婦岩から、日没の英虞湾へ。 */
  scenery: {
    tour: mieSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, MIE_FOOD_HULLS),
};
